import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { analyticsAPI, eventTypesAPI } from '../../services/api';
import { toast } from 'react-hot-toast';
import DashboardLayout from '../../components/layout/DashboardLayout';
import useDocumentTitle from '../../hooks/useDocumentTitle';

interface AnalyticsEvent {
  id: number;
  event_name: string;
  category: string;
  page_url: string;
  value: number | null;
  created_at: string;
}

interface EventType {
  id: number;
  name: string;
  display_name: string;
  category: string;
  description: string | null;
  has_value: boolean;
  value_type: 'number' | 'currency' | 'percentage' | null;
  value_label: string | null;
  color: string;
  icon: string;
  trigger_type: 'frontend' | 'backend' | 'both';
  require_fingerprint: boolean;
  require_user_id: boolean;
  is_active: boolean;
  event_count: number;
  unique_users: number;
  total_value: number;
}

interface EventTypesSummary {
  period: { days: number; start_date: string };
  totals: {
    total_events: number;
    total_unique_users: number;
    total_value: number;
  };
  event_types: EventType[];
}

const EventsPage: React.FC = () => {
  useDocumentTitle('Events');
  const { currentAsset } = useAuth();
  const [tab, setTab] = useState<'log' | 'points'>('log');

  // Event Log state
  const [events, setEvents] = useState<AnalyticsEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventPeriod, setEventPeriod] = useState('7d');

  // Registered Events state
  const [summary, setSummary] = useState<EventTypesSummary | null>(null);
  const [pointsLoading, setPointsLoading] = useState(false);
  const [pointsPeriod, setPointsPeriod] = useState('30d');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [newEventType, setNewEventType] = useState({
    name: '',
    display_name: '',
    category: 'custom',
    description: '',
    has_value: false,
    value_type: 'number' as 'number' | 'currency' | 'percentage',
    value_label: '',
    color: '#3B82F6',
    trigger_type: 'both' as 'frontend' | 'backend' | 'both',
    require_fingerprint: false,
    require_user_id: false,
  });

  const fetchEvents = useCallback(async () => {
    if (!currentAsset) return;
    setEventsLoading(true);
    try {
      const response = await analyticsAPI.getEvents(currentAsset.id, eventPeriod);
      const data = response.data.data || response.data || [];
      setEvents(Array.isArray(data) ? data : data.events || []);
    } catch (error: any) {
      console.error('Failed to fetch events:', error);
      toast.error('Failed to load events');
    } finally {
      setEventsLoading(false);
    }
  }, [currentAsset, eventPeriod]);

  const fetchEventTypesSummary = useCallback(async () => {
    setPointsLoading(true);
    try {
      const response = await eventTypesAPI.getSummary(pointsPeriod, currentAsset?.id);
      setSummary(response.data.data);
    } catch (error: any) {
      console.error('Failed to fetch event types summary:', error);
      toast.error('Failed to load event types');
    } finally {
      setPointsLoading(false);
    }
  }, [currentAsset, pointsPeriod]);

  useEffect(() => {
    if (tab === 'log') fetchEvents();
  }, [tab, fetchEvents]);

  useEffect(() => {
    if (tab === 'points') fetchEventTypesSummary();
  }, [tab, fetchEventTypesSummary]);

  const handleCreateEventType = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEventType.name || !newEventType.display_name) {
      toast.error('Name and display name are required');
      return;
    }
    setCreateLoading(true);
    try {
      await eventTypesAPI.create(newEventType);
      toast.success('Event point created successfully');
      setShowCreateModal(false);
      setNewEventType({
        name: '',
        display_name: '',
        category: 'custom',
        description: '',
        has_value: false,
        value_type: 'number',
        value_label: '',
        color: '#3B82F6',
        trigger_type: 'both',
        require_fingerprint: false,
        require_user_id: false,
      });
      fetchEventTypesSummary();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create event point');
    } finally {
      setCreateLoading(false);
    }
  };

  const formatNumber = (num: number) => new Intl.NumberFormat().format(num);

  const formatCurrency = (num: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD',
      minimumFractionDigits: 0, maximumFractionDigits: 2,
    }).format(num);

  const getCategoryBadgeClass = (category: string) => {
    switch (category) {
      case 'conversion': return 'bg-success';
      case 'revenue': return 'bg-warning text-dark';
      case 'engagement': return 'bg-info';
      default: return 'bg-secondary';
    }
  };

  const getTriggerTypeLabel = (type: string) => {
    switch (type) {
      case 'frontend': return 'JS Only';
      case 'backend': return 'API Only';
      case 'both': return 'JS & API';
      default: return type;
    }
  };

  return (
    <DashboardLayout>
      <div className="container-fluid">
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h4 className="fw-bold mb-0">
            <i className="bi bi-lightning-charge-fill me-2 text-warning"></i>Events
          </h4>
          {tab === 'points' && (
            <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
              <i className="bi bi-plus-lg me-2"></i>New Registered Event
            </button>
          )}
        </div>

        {/* Tabs */}
        <ul className="nav nav-pills mb-4">
          <li className="nav-item">
            <button
              className={`nav-link ${tab === 'log' ? 'active' : ''}`}
              onClick={() => setTab('log')}
            >
              <i className="bi bi-list-ul me-2"></i>Event Log
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${tab === 'points' ? 'active' : ''}`}
              onClick={() => setTab('points')}
            >
              <i className="bi bi-lightning me-2"></i>Register Events
            </button>
          </li>
        </ul>

        {/* === EVENT LOG TAB === */}
        {tab === 'log' && (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="btn-group">
                {['7d', '30d', '90d'].map(p => (
                  <button
                    key={p}
                    className={`btn btn-sm ${eventPeriod === p ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setEventPeriod(p)}
                  >
                    {p === '7d' ? '7 Days' : p === '30d' ? '30 Days' : '90 Days'}
                  </button>
                ))}
              </div>
              <Link to="/analytics/transactions" className="btn btn-sm btn-outline-primary">
                <i className="bi bi-arrow-right me-1"></i>View All Transactions
              </Link>
            </div>

            {eventsLoading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : events.length === 0 ? (
              <div className="text-center py-5">
                <i className="bi bi-lightning-charge fs-1 text-muted d-block mb-3"></i>
                <h5>No events recorded</h5>
                <p className="text-muted mb-4">Events will appear here once your tracking code fires them</p>
                <Link to="/analytics/transactions" className="btn btn-outline-primary">
                  View Transactions
                </Link>
              </div>
            ) : (
              <div className="card border-0 shadow-sm">
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Event Name</th>
                          <th>Category</th>
                          <th>Page URL</th>
                          <th className="text-end">Value</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {events.map((event, index) => (
                          <tr key={event.id || index}>
                            <td>
                              <span className="font-monospace fw-medium">{event.event_name}</span>
                            </td>
                            <td>
                              <span className={`badge ${getCategoryBadgeClass(event.category)}`}>
                                {event.category}
                              </span>
                            </td>
                            <td>
                              <small className="text-muted text-truncate d-block" style={{ maxWidth: '220px' }}>
                                {event.page_url}
                              </small>
                            </td>
                            <td className="text-end">
                              {event.value != null ? (
                                <span className="text-success fw-medium">{event.value}</span>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>
                            <td>
                              <small className="text-muted">
                                {new Date(event.created_at).toLocaleDateString('en-US', {
                                  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                                })}
                              </small>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* === EVENT POINTS TAB === */}
        {tab === 'points' && (
          <>
            {/* Period Selector */}
            <div className="d-flex gap-2 mb-4">
              <div className="btn-group">
                {['7d', '30d', '90d'].map(p => (
                  <button
                    key={p}
                    className={`btn btn-sm ${pointsPeriod === p ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setPointsPeriod(p)}
                  >
                    {p === '7d' ? '7 Days' : p === '30d' ? '30 Days' : '90 Days'}
                  </button>
                ))}
              </div>
            </div>

            {pointsLoading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <>
                {/* Summary Totals */}
                <div className="row g-3 mb-4">
                  <div className="col-12 col-md-4">
                    <div className="card border-0 shadow-sm">
                      <div className="card-body">
                        <div className="d-flex align-items-center">
                          <div className="p-3 bg-primary bg-opacity-10 text-primary rounded me-3">
                            <i className="bi bi-lightning-charge fs-3"></i>
                          </div>
                          <div>
                            <p className="text-muted small mb-1">Total Events</p>
                            <h3 className="h4 mb-0">{formatNumber(summary?.totals.total_events || 0)}</h3>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <div className="card border-0 shadow-sm">
                      <div className="card-body">
                        <div className="d-flex align-items-center">
                          <div className="p-3 bg-success bg-opacity-10 text-success rounded me-3">
                            <i className="bi bi-people fs-3"></i>
                          </div>
                          <div>
                            <p className="text-muted small mb-1">Unique Users</p>
                            <h3 className="h4 mb-0">{formatNumber(summary?.totals.total_unique_users || 0)}</h3>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <div className="card border-0 shadow-sm">
                      <div className="card-body">
                        <div className="d-flex align-items-center">
                          <div className="p-3 bg-warning bg-opacity-10 text-warning rounded me-3">
                            <i className="bi bi-currency-dollar fs-3"></i>
                          </div>
                          <div>
                            <p className="text-muted small mb-1">Total Value</p>
                            <h3 className="h4 mb-0">{formatCurrency(summary?.totals.total_value || 0)}</h3>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Event Types Table */}
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-white d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Registered Events ({summary?.event_types?.length || 0})</h5>
                    <Link to="/events" className="btn btn-sm btn-outline-secondary">
                      <i className="bi bi-arrow-up-right me-1"></i>Full View
                    </Link>
                  </div>
                  <div className="card-body p-0">
                    {summary?.event_types && summary.event_types.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-hover mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>Registered Event</th>
                              <th>Category</th>
                              <th>Trigger</th>
                              <th className="text-end">Events</th>
                              <th className="text-end">Users</th>
                              <th className="text-end">Value</th>
                              <th>Status</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {summary.event_types.map((eventType) => (
                              <tr key={eventType.id}>
                                <td>
                                  <Link to={`/events/${eventType.id}`} className="text-decoration-none">
                                    <div className="d-flex align-items-center">
                                      <div
                                        className="rounded-circle me-2 d-flex align-items-center justify-content-center"
                                        style={{
                                          width: '32px', height: '32px',
                                          backgroundColor: eventType.color + '20',
                                          color: eventType.color,
                                        }}
                                      >
                                        <i className={`bi bi-${eventType.icon || 'tag'}`}></i>
                                      </div>
                                      <div>
                                        <div className="fw-medium">{eventType.display_name}</div>
                                        <small className="text-muted font-monospace">{eventType.name}</small>
                                      </div>
                                    </div>
                                  </Link>
                                </td>
                                <td>
                                  <span className={`badge ${getCategoryBadgeClass(eventType.category)}`}>
                                    {eventType.category}
                                  </span>
                                </td>
                                <td>
                                  <small className="text-muted">{getTriggerTypeLabel(eventType.trigger_type)}</small>
                                </td>
                                <td className="text-end fw-medium">{formatNumber(eventType.event_count || 0)}</td>
                                <td className="text-end">{formatNumber(eventType.unique_users || 0)}</td>
                                <td className="text-end">
                                  {eventType.has_value ? (
                                    <span className="text-success fw-medium">
                                      {formatCurrency(eventType.total_value || 0)}
                                    </span>
                                  ) : (
                                    <span className="text-muted">—</span>
                                  )}
                                </td>
                                <td>
                                  {eventType.is_active ? (
                                    <span className="badge bg-success-subtle text-success">Active</span>
                                  ) : (
                                    <span className="badge bg-secondary-subtle text-secondary">Inactive</span>
                                  )}
                                </td>
                                <td>
                                  <Link to={`/events/${eventType.id}`} className="btn btn-sm btn-outline-primary">
                                    View
                                  </Link>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-5">
                        <i className="bi bi-lightning-charge fs-1 text-muted d-block mb-3"></i>
                        <h5>No Events Registered Yet</h5>
                        <p className="text-muted mb-4">
                          Create your first event point to start tracking custom events
                        </p>
                        <button
                          className="btn btn-primary"
                          onClick={() => setShowCreateModal(true)}
                        >
                          <i className="bi bi-plus-lg me-2"></i>Create Registered Event
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Create Event Type Modal */}
      {showCreateModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Create Registered Event</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowCreateModal(false)}
                ></button>
              </div>
              <form onSubmit={handleCreateEventType}>
                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Event Name (slug) <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control font-monospace"
                        placeholder="e.g., signup, purchase, form_submit"
                        value={newEventType.name}
                        onChange={(e) => setNewEventType({
                          ...newEventType,
                          name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_')
                        })}
                        required
                      />
                      <small className="text-muted">Lowercase letters, numbers, underscores only</small>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Display Name <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g., User Sign Up"
                        value={newEventType.display_name}
                        onChange={(e) => setNewEventType({ ...newEventType, display_name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Category</label>
                      <select
                        className="form-select"
                        value={newEventType.category}
                        onChange={(e) => setNewEventType({ ...newEventType, category: e.target.value })}
                      >
                        <option value="custom">Custom</option>
                        <option value="conversion">Conversion</option>
                        <option value="revenue">Revenue</option>
                        <option value="engagement">Engagement</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Color</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          value={newEventType.color}
                          onChange={(e) => setNewEventType({ ...newEventType, color: e.target.value })}
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={newEventType.color}
                          onChange={(e) => setNewEventType({ ...newEventType, color: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="col-12">
                      <label className="form-label">Description</label>
                      <textarea
                        className="form-control"
                        rows={2}
                        placeholder="Optional description"
                        value={newEventType.description}
                        onChange={(e) => setNewEventType({ ...newEventType, description: e.target.value })}
                      />
                    </div>
                    <div className="col-12">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="eventsHasValue"
                          checked={newEventType.has_value}
                          onChange={(e) => setNewEventType({ ...newEventType, has_value: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="eventsHasValue">
                          Track monetary/numeric value with this event
                        </label>
                      </div>
                    </div>
                    {newEventType.has_value && (
                      <>
                        <div className="col-md-6">
                          <label className="form-label">Value Type</label>
                          <select
                            className="form-select"
                            value={newEventType.value_type}
                            onChange={(e) => setNewEventType({
                              ...newEventType,
                              value_type: e.target.value as 'number' | 'currency' | 'percentage'
                            })}
                          >
                            <option value="currency">Currency (Money)</option>
                            <option value="number">Number</option>
                            <option value="percentage">Percentage</option>
                          </select>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Value Label</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="e.g., Amount, Price, Score"
                            value={newEventType.value_label}
                            onChange={(e) => setNewEventType({ ...newEventType, value_label: e.target.value })}
                          />
                        </div>
                      </>
                    )}
                    <div className="col-md-6">
                      <label className="form-label">Trigger Type</label>
                      <select
                        className="form-select"
                        value={newEventType.trigger_type}
                        onChange={(e) => setNewEventType({
                          ...newEventType,
                          trigger_type: e.target.value as 'frontend' | 'backend' | 'both'
                        })}
                      >
                        <option value="both">Both (JS & Backend API)</option>
                        <option value="frontend">Frontend Only (JavaScript)</option>
                        <option value="backend">Backend Only (Server API)</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowCreateModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={createLoading}>
                    {createLoading ? (
                      <><span className="spinner-border spinner-border-sm me-2"></span>Creating...</>
                    ) : (
                      <><i className="bi bi-plus-lg me-2"></i>Create Registered Event</>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default EventsPage;
